(ns dashboard.entity.analysis.db
  (:require [clojure.set :as set]
            [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [io.repliance.util.tree :as tree]
            [re-frame.core :as rf]))

;; ----------------------------------------------------------------------------
;;  Top-Level Analyses
;;
;; These subscriptions are not used directly, but rather as top-level
;; dependancies for analysis-related content across the app
;; ----------------------------------------------------------------------------

(rf/reg-sub
 ::structured-analyses
 (fn [db]
   (:structured-analyses db)))

(rf/reg-sub
 ::applied-analyses
 (fn [db]
   (:applied-analyses db)))

(rf/reg-sub
 ::applied-analyses-data
 :<- [::applied-analyses]
 (fn [applied-analyses]
   (get applied-analyses :data)))

(rf/reg-sub
 ::version-id->structured-analysis
 :<- [::structured-analyses]
 (fn [_ structured-analyses]
   (:version-id->structured-analysis structured-analyses {})))

;; ----------------------------------------------------------------------------
;;  Subscriptions and their Getters
;; ----------------------------------------------------------------------------

(rf/reg-sub
 ::structured-analyses-data
 :<- [::structured-analyses]
 (fn [structured-analyses]
   (get structured-analyses :data)))

(defn structured-analyses-data*
  []
  (rf/subscribe [::structured-analyses-data]))

(rf/reg-sub
 ::structured-analysis
 :<- [::structured-analyses-data]
 :<- [::applied-analyses-data]
 (fn [[structured-analyses applied-analyses] [_ construct-id version-id]]
   (let [all-versions (get structured-analyses construct-id)
         structured-analysis (if version-id
                               (first (filter #(= version-id (:version-id %)) all-versions))
                               (first all-versions))]
     (when structured-analysis
       (assoc structured-analysis :applied-analyses (get applied-analyses (:construct-id structured-analysis)))))))

(defn structured-analysis*
  ([construct-id]
   (rf/subscribe [::structured-analysis construct-id]))
  ([construct-id version-id]
   (rf/subscribe [::structured-analysis construct-id version-id])))

(rf/reg-sub
 ::analysis-list
 :<- [::common-subs/features-enabled]
 :<- [::structured-analyses-data]
 :<- [::applied-analyses-data]
 :<- [::version-id->structured-analysis]
 (fn [[features-enabled structured-analyses applied-analyses version-id->structured-analysis]]
   (-> structured-analyses
       (->> (map (fn [[_construct-id structured-analyses]]
                   (let [{:keys [construct-id] :as latest} (first structured-analyses)
                         previous-versions (drop 1 structured-analyses)]
                     (assoc latest
                            :applied-analyses
                            (map (fn [{:keys [structured-analysis-version-uuid] :as applied-analysis}]
                                   (assoc applied-analysis
                                          :structured-analysis
                                          (version-id->structured-analysis structured-analysis-version-uuid)))
                                 (get applied-analyses construct-id))
                            :previous-versions
                            previous-versions
                            :view/features-enabled features-enabled)))))
       (tree/sort-hierarchical (juxt :parent-id :construct) ;sort-keyfn
                               compare
                               :construct-id))))
(defn analysis-list*
  []
  (rf/subscribe [::analysis-list]))

(rf/reg-sub
 ::applied-analysis
 :<- [::applied-analyses-data]
 (fn [applied-analyses [_ construct-id version-id]]
   (let [all-versions (get applied-analyses construct-id)]
     (if version-id
       (first (filter #(= version-id (:version-id %)) all-versions))
       (first all-versions)))))

(defn applied-analysis*
  ([construct-id]
   (rf/subscribe [::applied-analysis construct-id]))
  ([construct-id version-id]
   (rf/subscribe [::applied-analysis construct-id version-id])))

(rf/reg-sub
 ::sub-analyses
 :<- [::structured-analyses-data]
 :<- [::applied-analyses-data]
 (fn [[structured-analyses applied-analyses] [_ construct-id]]
   (if (nil? construct-id)
     []
     (let [latest-analyses (sort-by :construct-id (map first (vals structured-analyses)))
           latest-analyses (map (fn [structured-analysis]
                                  (assoc structured-analysis :applied-analyses (get applied-analyses (:construct-id structured-analysis))))
                                latest-analyses)
           parent-id->analysis (group-by :parent-id latest-analyses)]
       (get parent-id->analysis construct-id)))))

(defn sub-analyses*
  [construct-id]
  (rf/subscribe [::sub-analyses construct-id]))

(rf/reg-sub
 ::hierarchy
 :<- [::structured-analyses-data]
 (fn [structured-analyses [_ construct-id]]
   (let [latest-analyses (map (fn [[_id versions]]
                                (let [latest (first versions)]
                                  (assoc latest :previous-versions (rest versions))))
                              structured-analyses)
         root-id (tree/find-root-id construct-id latest-analyses :construct-id)]
     (tree/sort-hierarchical (tree/filter-tree root-id latest-analyses :construct-id)
                             :construct-id
                             compare
                             :construct-id))))

(defn hierarchy*
  [construct-id]
  (rf/subscribe [::hierarchy construct-id]))

; Has both of dependancies and subscribers
(rf/reg-sub
 ::answer-id->applied-analyses
 :<- [::applied-analyses]
 (fn [applied-analyses]
   (:answer-id->applied-analyses applied-analyses {})))

(defn answer-id->applied-analyses*
  []
  (rf/subscribe [::answer-id->applied-analyses]))

;; ------------------

(rf/reg-sub
 ::applied-analyses-for-answer
 :<- [::applied-analyses]
 (fn [applied-analyses [_ answer-id]]
   (get-in applied-analyses [:answer-id->applied-analyses answer-id])))

(defn db->applied-analyses-for-answer [db answer-id]
  (get-in db [:applied-analyses :answer-id->applied-analyses answer-id]))

(defn applied-analyses-for-answer*
  [answer-id]
  (rf/subscribe [::applied-analyses-for-answer answer-id]))

;; ------------------

(rf/reg-sub
 ::answer-id->structured-analysis
 :<- [::answer-id->applied-analyses]
 :<- [::structured-analyses-with-applied-joined]
 (fn [[answer-id->applied-analyses structured-analyses-with-applied-joined] [_ answer-id]]
   (let [construct-ids (->> (get answer-id->applied-analyses answer-id)
                            (mapv :construct-id)
                            set)]
     (->> structured-analyses-with-applied-joined
          (filterv (comp (partial contains? construct-ids) :construct-id))))))

(defn answer-id->structured-analysis*
  [answer-id]
  (rf/subscribe [::answer-id->structured-analysis answer-id]))

;; ------------------

(rf/reg-sub
 ::structured-analyses-with-applied-joined
 :<- [::common-subs/features-enabled]
 :<- [::structured-analyses-data]
 :<- [::applied-analyses-data]
 :<- [::version-id->structured-analysis]
 (fn [[features-enabled structured-analyses applied-analyses version-id->structured-analysis]]
   (map (fn [[_construct-id structured-analyses]]
          (let [{:keys [construct-id] :as latest} (first structured-analyses)
                previous-versions (drop 1 structured-analyses)]
            (assoc latest
                   :applied-analyses
                   (map (fn [{:keys [structured-analysis-version-uuid] :as applied-analysis}]
                          (assoc applied-analysis
                                 :structured-analysis
                                 (version-id->structured-analysis structured-analysis-version-uuid)))
                        (get applied-analyses construct-id))
                   :previous-versions
                   previous-versions
                   :view/features-enabled features-enabled
                   :paginated-list/row-key construct-id)))
        structured-analyses)))

(defn structured-analyses-with-applied-joined*
  []
  (rf/subscribe [::structured-analyses-with-applied-joined]))

;; ------------------

(rf/reg-sub
 ::loading-structured-or-applied
 :<- [::structured-analyses]
 :<- [::applied-analyses]
 (fn [[structured-analyses applied-analyses] _]
   (or (get structured-analyses :loading)
       (get applied-analyses :loading))))

(defn loading-structured-or-applied*
  []
  (rf/subscribe [::loading-structured-or-applied]))

;; ------------------

(rf/reg-sub
 ::construct-id->categories
 :<- [::structured-analyses-data]
 (fn [structured-analyses-data _]
   (-> structured-analyses-data
       (update-vals (comp set (partial mapv :name) :categories first)))))

(defn construct-id->categories*
  []
  (rf/subscribe [::construct-id->categories]))

;; ----------------------------------------------------------------------------
;;  Effects and their Callers
;; ----------------------------------------------------------------------------

(rf/reg-event-db
 ::get-structured-analyses-success
 (fn [db [_ structured-analyses]]
   (assoc db :structured-analyses
          {:loading false
           :data (->> structured-analyses
                      (sort-by :structured-analysis-id #(compare %2 %1))
                      (group-by :construct-id))
           ;; Create a lookup structure, for resolving the structured analysis referred to by applied analysis
           ;; Used for example in the answer bank view
           :version-id->structured-analysis (into {}
                                                  (map (juxt :version-id identity))
                                                  structured-analyses)})))
(rf/reg-event-fx
 ::get-structured-analyses
 (fn [{:keys [db]} [_ only-if-not-present]]
   (when (or (not only-if-not-present)
             (not (get-in db [:structured-analyses :data])))
     {:db (assoc-in db [:structured-analyses :loading] true)
      :http-xhrio {:method :get
                   :uri "/api/structured-analysis/all-versions"
                   :response-format http-config/transit-response-format
                   :on-success [::get-structured-analyses-success]
                   :on-failure [::ui/set-error "Failed to get structured analyses"]}})))

(defn get-structured-analyses
  [only-if-not-present]
  (rf/dispatch [::get-structured-analyses only-if-not-present]))

;; ------------------

(rf/reg-event-db
 ::get-applied-analyses-success
 (fn [db [_ applied-analyses]]
   (assoc db
          :applied-analyses
          {:loading false
           :data (->> applied-analyses
                      (sort-by :applied-structured-analysis-id #(compare %2 %1))
                      (group-by :construct-id))
           ;; Create a lookup structure to the answer bank view,
           ;; where we need to show the applied analyses for each answer of a question
           :answer-id->applied-analyses
           (reduce (fn [acc {:keys [answer-ids] :as applied-analysis}]
                     (reduce (fn [acc2 answer-id]
                               (update acc2 answer-id
                                       (fn [answer-applied-analyses]
                                         ;; Sort by construct identifier first, then applied analysis version
                                         ;; This way we have under each construct, the newest applied analyses first
                                         (sort #(compare [(:construct-id %1) (:applied-structured-analysis-id %2)]
                                                         [(:construct-id %2) (:applied-structured-analysis-id %1)])
                                               (conj (or answer-applied-analyses []) applied-analysis)))))
                             acc
                             answer-ids))
                   {}
                   applied-analyses)})))

(rf/reg-event-fx
 ::get-applied-analyses
 (fn [{:keys [db]} [_ only-if-not-present]]
   (when (or (not only-if-not-present)
             (not (get-in db [:applied-analyses :data])))
     {:db (assoc-in db [:applied-analyses :loading] true)
      :http-xhrio {:method :get
                   :uri "/api/applied-structured-analysis/"
                   :response-format http-config/transit-response-format
                   :on-success [::get-applied-analyses-success]
                   :on-failure [::ui/set-error "Failed to get applied analyses"]}})))

(defn get-applied-analyses
  [only-if-not-present]
  (rf/dispatch [::get-applied-analyses only-if-not-present]))

;; ------------------

(rf/reg-event-fx
 ::get-structured-analysis-questions-success
 (fn [{:keys [db]} [_ after data]]
   (cond-> {:db (assoc db :structured-analysis-questions
                       {:loading false
                        :load-success true
                        :data (group-by :question-id data)})}
     after (assoc :dispatch after))))

(rf/reg-event-fx
 ::get-structured-analysis-questions-failure
 (fn [{:keys [db]} [_ after]]
   {:db (update-in db [:structured-analysis-questions]
                   assoc
                   :loading false
                   :load-success false)
    :fx (cond-> [[:dispatch [::notifications/add
                             {:message "Failed to get questions linked to structured analyses"
                              :class "is-danger"}]]]
          after (conj [:dispatch after]))}))

(rf/reg-event-fx
 ::get-structured-analysis-questions
 (fn [{:keys [db]} [_ after only-if-not-present]]
   (when (not (and only-if-not-present
                   (get-in db [:categories :data])))
     {:db (assoc-in db [:structured-analysis-questions :loading] true)
      :http-xhrio {:method :get
                   :uri "/api/structured-analysis/questions"
                   :response-format http-config/transit-response-format
                   :on-success [::get-structured-analysis-questions-success (:success after)]
                   :on-failure [::get-structured-analysis-questions-failure (:failure after)]}})))

(defn get-structured-analysis-questions
  ([only-if-not-present]
   (rf/dispatch [::get-structured-analysis-questions nil only-if-not-present]))
  ([success-and-fail only-if-not-present]
   (rf/dispatch [::get-structured-analysis-questions success-and-fail only-if-not-present])))

;; ------------------
;; ::link-analysis-success is externally references and that is ugly!
(rf/reg-event-fx
 ::link-analysis-success
 (fn [{:keys [db]} [_ set-load-state questionnaire-id analyses question-id->answer]]
   (set-load-state (map :version-id analyses) false)
   {:db (reduce (fn [acc [question-id answer]]
                  (assert (or (uuid? answer)
                              (map? answer))
                          "Argument should be either Answer map or Answer UUID")
                  (reduce (fn [acc2 {:keys [construct-id] applied-analysis-version-id :version-id}]
                            (let [answer-id (if (uuid? answer)
                                              answer
                                              (:id answer))
                                  updated-applied-analysis (-> (filter
                                                                #(= (:version-id %) applied-analysis-version-id)
                                                                (get-in db [:applied-analyses :data construct-id]))
                                                               first
                                                               (update :answer-ids conj answer-id))]
                              (-> acc2
                                  (update-in [:questionnaires
                                              questionnaire-id
                                              :questions
                                              :data]
                                             (fn [questions]
                                               (mapv (fn [question]
                                                       (if (= question-id (:id question))
                                                         (if (uuid? answer)
                                                           (update-in question [:selected-answer :applied-analyses] (fnil conj []) updated-applied-analysis)
                                                           (assoc question :selected-answer (assoc answer :applied-analyses [updated-applied-analysis])))
                                                         question))
                                                     questions)))
                                  (update-in [:applied-analyses
                                              :data
                                              construct-id]
                                             (fn [applied-analyses]
                                               (mapv (fn [{:keys [version-id] :as applied-analysis}]
                                                       (if (= version-id applied-analysis-version-id)
                                                         (update applied-analysis :answer-ids conj answer-id)
                                                         applied-analysis))
                                                     applied-analyses)))
                                  (update-in [:applied-analyses
                                              :answer-id->applied-analyses
                                              answer-id]
                                             (fn [answer-applied-analyses]
                                               (assert (-> updated-applied-analysis first seq))
                                               (sort #(compare [(:construct-id %1) (:applied-structured-analysis-id %2)]
                                                               [(:construct-id %2) (:applied-structured-analysis-id %1)])
                                                     (conj (or answer-applied-analyses []) updated-applied-analysis)))))))
                          acc
                          analyses))
                db
                question-id->answer)
    :dispatch [::notifications/add {:message (str (if (> (count analyses) 1)
                                                    "Analyses"
                                                    "Analysis")
                                                  " linked")}]}))
(defn link-analysis-success
  [set-load-state questionnaire-id analyses question-id->answer]
  (rf/dispatch [::link-analysis-success set-load-state questionnaire-id analyses question-id->answer]))

(rf/reg-event-fx
 ::link-analysis-failure
 (fn [_ [_ set-load-state analyses]]
   (set-load-state (map :version-id analyses) false)
   {:dispatch [::notifications/add {:message "Failed to link analysis to question answer"
                                    :class "is-danger"}]}))

(rf/reg-event-fx
 ::link-analysis
 (fn [_ [_ set-load-state questionnaire-id question-id analyses]]
   (set-load-state (map :version-id analyses) true)
   {:http-xhrio {:method :post
                 :uri "/api/questions/analysis"
                 :params {question-id (set (map :version-id analyses))}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::link-analysis-success set-load-state questionnaire-id analyses]
                 :on-failure [::link-analysis-failure set-load-state analyses]}}))

(defn link-analysis
  [set-load-state questionnaire-id question-id analyses]
  (rf/dispatch [::link-analysis set-load-state questionnaire-id question-id analyses]))

;; ------------------

(rf/reg-event-fx
 ::unlink-applied-analysis-success
 (fn [{:keys [db]} [_ questionnaire-id question-id answer-id construct-id applied-analysis-version-id set-linking {:keys [removed-answer-ids]}]]
   (set-linking false)
   {:db (-> db
            (update-in [:questionnaires questionnaire-id :questions :data]
                       (fn [questions]
                         (mapv (fn [{:keys [id selected-answer] :as question}]
                                 (if (and (= question-id id)
                                          (contains? removed-answer-ids (:id selected-answer)))
                                   (dissoc question :selected-answer)
                                   question))
                               questions)))
            (update-in [:applied-analyses :data construct-id]
                       (fn [applied-analyses]
                         (mapv (fn [{:keys [version-id] :as applied-analysis}]
                                 (if (= version-id applied-analysis-version-id)
                                   (update applied-analysis :answer-ids set/difference (cond-> removed-answer-ids
                                                                                         answer-id (conj answer-id)))
                                   applied-analysis))
                               applied-analyses)))
            (update-in [:applied-analyses :answer-id->applied-analyses answer-id]
                       (fn [applied-analyses]
                         (remove #(= applied-analysis-version-id (:version-id %))  applied-analyses))))
    :dispatch [::notifications/add {:message "Analysis unlinked"}]}))

(rf/reg-event-fx
 ::unlink-applied-analysis-failure
 (fn [_ [_ set-linking]]
   (set-linking false)
   {:dispatch [::notifications/add {:message "Failed to unlink analysis"
                                    :class "is-danger"}]}))

(rf/reg-event-fx
 ::unlink-applied-analysis
 (fn [_ [_ questionnaire-id question-id answer-id construct-id applied-analysis-version-id set-linking]]
   (set-linking true)
   {:http-xhrio {:method :delete
                 :uri "/api/questions/analysis"
                 :params {question-id #{applied-analysis-version-id}}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::unlink-applied-analysis-success questionnaire-id question-id answer-id construct-id applied-analysis-version-id set-linking]
                 :on-failure [::unlink-applied-analysis-failure set-linking]}}))

(defn unlink-applied-analysis
  [questionnaire-id question-id answer-id construct-id applied-analysis-version-id set-linking]
  (rf/dispatch [::unlink-applied-analysis
                questionnaire-id
                question-id
                answer-id
                construct-id
                applied-analysis-version-id
                set-linking]))

;; ------------------

(rf/reg-event-db
 ::get-categories-success
 (fn [db [_ categories]]
   (let [categories-by-id (into {} (map (juxt :id identity)) categories)]
     (assoc-in db [:categories] {:loading false
                                 :data categories-by-id}))))

(rf/reg-event-fx
 ::get-categories
 (fn [{:keys [db]} [_ only-if-not-present]]
   (when (not (and only-if-not-present
                   (get-in db [:categories :data])))
     {:db (assoc-in db [:categories :loading] true)
      :http-xhrio {:method :get
                   :uri "/api/category"
                   :response-format http-config/transit-response-format
                   :on-success [::get-categories-success]
                   :on-failure [::ui/set-error "Failed to get analysis categories"]}})))

(defn get-categories
  [only-if-not-present]
  (rf/dispatch [::get-categories only-if-not-present]))

;; ----------------------------------------------------------------------------
;;  Utility Functions
;; ----------------------------------------------------------------------------

(defn is-latest-asa-outdated?
  [structured-analysis applied-atructured-analyses]
  (not= (:construct-version structured-analysis)
        (-> applied-atructured-analyses first :construct-version)))

(defn drop-nth [n coll]
  (keep-indexed #(when-not (= %1 n) %2) coll))